import { computeRosaSummary, primaryMantraRole } from './rosaCalc.js';
import { resolveStudioPlayerMantraRoles } from './studioCalc.js';
import { escapeHtml } from '../utils/escapeHtml.js';
import { playerToKey } from '../utils/playerKey.js';

const REF_ROSTER = 29;
const REF_GK = 3;

/** Portieri e movimento per rosa (stessa logica di rosaModuleTargets). */
export function computeRosterGkMovimentoPerTeam(maxPlayers) {
  const max = Math.max(0, Number(maxPlayers) || 0);
  if (max === 0) return { gk: 0, movimento: 0 };
  let gk = Math.round((REF_GK * max) / REF_ROSTER);
  gk = Math.min(max, Math.max(0, gk));
  if (max >= 1 && gk === 0) gk = 1;
  return { gk, movimento: max - gk };
}

/** Es. «29x8» → 29 giocatori per rosa, 8 partecipanti all’asta. */
export function parseAuctionNxM(name) {
  const m = String(name || '').match(/(\d+)\s*[x×]\s*(\d+)/i);
  if (!m) return null;
  const a = Number(m[1]);
  const b = Number(m[2]);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return null;
  return {
    rosterMax: Math.max(a, b),
    participants: Math.min(a, b),
  };
}

export function resolveAuctionRosterSize({ maxPlayers, auctionName }) {
  const fromName = parseAuctionNxM(auctionName);
  if (fromName) return fromName.rosterMax;
  return Math.max(0, Number(maxPlayers) || 0);
}

export function resolveAuctionParticipants({ numParticipants, maxPlayers, auctionName }) {
  const explicit = Number(numParticipants);
  if (Number.isFinite(explicit) && explicit > 0) {
    return Math.floor(explicit);
  }
  const fromName = parseAuctionNxM(auctionName);
  return fromName ? fromName.participants : null;
}

/** Un target Studio = un giocatore: portiere se ha P, altrimenti movimento. */
export function classifyStudioTargetPool(player) {
  const roles = resolveStudioPlayerMantraRoles(player);
  if (roles.includes('P')) return 'portieri';
  return 'movimento';
}

export function countStudioTargetsByPool(studioTargetEntries = []) {
  let portieri = 0;
  let movimento = 0;
  for (const entry of studioTargetEntries) {
    const bucket = classifyStudioTargetPool(entry.player);
    if (bucket === 'portieri') portieri += 1;
    else movimento += 1;
  }
  return { portieri, movimento };
}

export function computeAuctionPoolToBuy({ participants, maxPlayers }) {
  const part = Math.max(0, Number(participants) || 0);
  const { gk: gkPerTeam, movimento: movPerTeam } = computeRosterGkMovimentoPerTeam(maxPlayers);
  return {
    portieri: part * gkPerTeam,
    movimento: part * movPerTeam,
  };
}

export function buildStudioKpiHint({
  studioSpendItems = [],
  maxPlayers = 0,
  auctionName = '',
  numParticipants = null,
}) {
  const participants = resolveAuctionParticipants({ numParticipants, maxPlayers, auctionName });
  if (!participants) {
    return {
      line: 'Imposta i partecipanti in Impostazioni per vedere quanti giocatori servono.',
    };
  }
  const rosterSize = resolveAuctionRosterSize({ maxPlayers, auctionName });
  const toBuy = computeAuctionPoolToBuy({ participants, maxPlayers: rosterSize });
  const inStudio = countStudioTargetsByPool(studioSpendItems);
  return {
    line: `P ${inStudio.portieri}/${toBuy.portieri} · Mov ${inStudio.movimento}/${toBuy.movimento}`,
  };
}

/**
 * @param {{ entries: Array<{key:string}>, roleTargets: Record<string, number>, playersByKey: Map<string, {roles?: string[]}> }} args
 * @returns {string} es. "1 DS · 1 DC" oppure ""
 */
export function computeRoleTargetGaps({ entries, roleTargets, playersByKey }) {
  const ownedByRole = Object.create(null);
  for (const entry of entries || []) {
    const player = playersByKey?.get(entry.key);
    const role = primaryMantraRole(player?.roles || []);
    if (!role) continue;
    ownedByRole[role] = (ownedByRole[role] || 0) + 1;
  }
  const parts = [];
  for (const [role, target] of Object.entries(roleTargets || {})) {
    const t = Number(target) || 0;
    if (t <= 0) continue;
    const owned = ownedByRole[role] || 0;
    const deficit = t - owned;
    if (deficit > 0) parts.push(`${deficit} ${role}`);
  }
  return parts.join(' · ');
}

/**
 * @returns {{
 *   crediti: { value: number, unit: string, hint: string },
 *   studio: { value: number, unit: string, hint: string },
 *   rosa: { valueLabel: string, hint: string },
 * }}
 */
export function buildListaKpiModel({
  budget,
  maxPlayers,
  rosterEntries = [],
  roleTargets = {},
  playersByKey = new Map(),
  studioTargetCount = 0,
  studioSpendItems = [],
  auctionName = '',
  numParticipants = null,
}) {
  const summary = computeRosaSummary({
    entries: rosterEntries,
    budget,
    roleTargets,
    maxPlayers,
    playersByKey,
  });
  const open = Math.max(0, summary.slots - summary.owned);
  const perOpenSlot = open === 0 ? null : Math.round(summary.residui / open);

  return {
    crediti: {
      value: summary.residui,
      unit: 'crediti',
      hint:
        perOpenSlot == null
          ? 'Nessun posto libero'
          : `${perOpenSlot} crediti per ognuno dei ${open} posti liberi`,
    },
    studio: {
      value: Number(studioTargetCount) || 0,
      unit: 'target',
      hint: buildStudioKpiHint({
        studioSpendItems,
        maxPlayers,
        auctionName,
        numParticipants,
      }),
    },
    rosa: {
      valueLabel: `${summary.owned}/${summary.slots}`,
      hint: open === 0 ? 'Rosa al completo' : `${open} posti liberi`,
    },
  };
}

export const listaKpiMethods = {
  renderListaKpiStrip() {
    const el = document.getElementById('lista-kpi-strip');
    if (!el) return;

    const isDesktop = window.matchMedia('(min-width: 761px)').matches;
    if (!isDesktop || !this.state.activeAuctionId) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }

    const rosterEntries = Array.from(this.state.roster || [], ([key, entry]) => ({
      key,
      price: entry?.price,
    }));
    const playersByKey = new Map(
      (this.state.allPlayers || []).map((player) => [playerToKey(player), player]).filter(([k]) => k),
    );

    const studioPoolEntries = [];
    if (this.state.studioTargets instanceof Map) {
      for (const playerKey of this.state.studioTargets.keys()) {
        const player = playersByKey.get(playerKey);
        studioPoolEntries.push({ player: player || null });
      }
    }

    const model = buildListaKpiModel({
      budget: Number(this.state.rosaBudget) || 0,
      maxPlayers: Number(this.state.rosaMaxPlayers) || 0,
      rosterEntries,
      roleTargets: this.state.rosaRoleTargets || {},
      playersByKey,
      studioTargetCount: this.state.studioTargets?.size || 0,
      studioSpendItems: studioPoolEntries,
      auctionName: this.state.activeAuctionName || '',
      numParticipants: this.state.rosaNumParticipants,
    });

    el.hidden = false;
    el.innerHTML = `
      <article class="lista-kpi-card" data-testid="lista-kpi-crediti">
        <div class="lista-kpi-card-top">
          <span class="lista-kpi-card-title">Crediti residui</span>
          <button type="button" class="lista-kpi-btn lista-kpi-btn--primary" data-lista-kpi-action="settings">Impostazioni</button>
        </div>
        <div class="lista-kpi-metric">
          <span class="lista-kpi-value num">${escapeHtml(String(model.crediti.value))}</span>
          <span class="lista-kpi-unit">${escapeHtml(model.crediti.unit)}</span>
        </div>
      </article>
      <article class="lista-kpi-card" data-testid="lista-kpi-studio">
        <div class="lista-kpi-card-top">
          <span class="lista-kpi-card-title">Target in Studio</span>
          <button type="button" class="lista-kpi-btn" data-lista-kpi-action="studio">Apri Studio</button>
        </div>
        <div class="lista-kpi-metric lista-kpi-metric--split">
          <div class="lista-kpi-metric-primary">
            <span class="lista-kpi-value num">${escapeHtml(String(model.studio.value))}</span>
            <span class="lista-kpi-unit">${escapeHtml(model.studio.unit)}</span>
          </div>
          <span class="lista-kpi-side-note num">${escapeHtml(model.studio.hint.line)}</span>
        </div>
      </article>
      <article class="lista-kpi-card" data-testid="lista-kpi-rosa">
        <div class="lista-kpi-card-top">
          <span class="lista-kpi-card-title">Rosa</span>
          <button type="button" class="lista-kpi-btn" data-lista-kpi-action="rosa">Apri Rosa</button>
        </div>
        <div class="lista-kpi-metric">
          <span class="lista-kpi-value num">${escapeHtml(model.rosa.valueLabel)}</span>
        </div>
      </article>
    `;

    el.querySelector('[data-lista-kpi-action="settings"]')?.addEventListener('click', () => {
      this.openAuctionSettings?.();
    });
    el.querySelector('[data-lista-kpi-action="studio"]')?.addEventListener('click', () => {
      this.switchAuctionScreen?.('studio');
    });
    el.querySelector('[data-lista-kpi-action="rosa"]')?.addEventListener('click', () => {
      this.switchAuctionScreen?.('rosa');
    });
  },
};
